/**
 * The catalogue: built-in modules and Firestore ones, resolved into one list.
 *
 * Three admin pages need the same answer to "what modules exist" — Modules,
 * Lessons and Courses — and each had grown its own copy of the merge. They are
 * the sort of copies that drift, and a page that disagrees with its neighbour
 * about whether a module exists is worse than one that is simply wrong.
 *
 * A module authored in AI Studio under the SAME id as a built-in one overrides
 * it rather than sitting beside it; that is how a code-authored module gets
 * edited without a deploy, and ModuleRouter honours the same precedence when it
 * renders. So the merge is: built-ins in registry order, each replaced by its
 * override where one exists, then whatever custom modules are left.
 */

import { publishedModules, modules as builtInModules } from './modules.js'

/**
 * Is this course hidden from trainees?
 *
 * Absent means published, matching the built-in module convention — so every
 * course that exists today stays visible with nothing to migrate.
 */
export function isCoursePublished(course) {
  if (!course) return false
  return course.published !== false
}

/**
 * Does this course have anything a trainee could actually open?
 *
 * FOUR near-identical copies of this predicate had grown — in Home, the
 * Degrees admin page, `coursesForDegrees` and the course page — which is three
 * too many for a rule that decides what a trainee can see. They are collapsed
 * here, keeping the same OR: a published built-in in this course, or a
 * published custom one naming it.
 */
export function courseHasPublishedModules(course, customModulesList = []) {
  // Accepts a course OR a bare id: several callers only hold the id, and a
  // course with no list behaves identically either way.
  const c = typeof course === 'string' ? { id: course } : course
  if (!c?.id) return false
  if (Array.isArray(c.modules)) {
    return publishedInCourse(c, mergeCatalogue(builtInModules, customModulesList || [])).length > 0
  }
  if (publishedModules(c.id).length > 0) return true
  return (customModulesList || []).some((m) => m.track === c.id && m.status === 'published')
}

/**
 * What a trainee may see: not hidden, AND with something in it.
 *
 * Admin pages deliberately do NOT use this — an admin has to be able to see a
 * hidden course to unhide it, and the Degrees page has to keep showing one a
 * degree still contains. Hiding it there is how a course silently disappears
 * from a degree that never stopped including it.
 */
export function isCourseVisible(course, customModulesList = []) {
  if (!course) return false
  return isCoursePublished(course) && courseHasPublishedModules(course, customModulesList)
}

/**
 * The ids of the modules in a course, IN ORDER.
 *
 * `course.modules` is the authority: an ordered array of module ids owned by
 * the course. That is what makes a module able to sit in more than one course
 * at a time — membership is a fact about the course, not a field on the module
 * — and it is what gives "number by position" a position to count.
 *
 * When a course has no list yet, membership falls back to the module's own
 * `track`, in catalogue order. That is the ENTIRE migration: the 25 built-in
 * modules and every existing Firestore document keep working untouched, and a
 * course gains a list the first time somebody edits its modules.
 */
export function courseModuleIds(course, catalogue = []) {
  if (Array.isArray(course?.modules)) return course.modules.map(String)
  return (catalogue || [])
    .filter((m) => String(m.track) === String(course?.id))
    .map((m) => String(m.id))
}

/**
 * The modules in a course, in order, as objects.
 *
 * Ids naming a module that no longer exists are dropped rather than left as
 * holes — a deleted module should not renumber the ones after it into a gap.
 */
export function courseRoster(course, catalogue = []) {
  const byId = new Map((catalogue || []).map((m) => [String(m.id), m]))
  return courseModuleIds(course, catalogue).map((id) => byId.get(id)).filter(Boolean)
}

/**
 * What the trainee is told a module is called in THIS course: "Module 3".
 *
 * Position, not a field — so a module in two courses is numbered correctly in
 * both, which a single `num` on the module could never do.
 *
 * `startsAt` exists because Sheets Reporting deliberately opens at Module 0
 * (the big-picture "Where Does Our Data Come From?"), while every other course
 * starts at 1. Position alone would renumber all seventeen Sheets modules under
 * thirteen trainees mid-course. The offset is per course and defaults to 1.
 */
export function moduleNumberIn(course, index) {
  const start = Number.isFinite(course?.startsAt) ? course.startsAt : 1
  return index < 0 ? null : start + index
}

/**
 * Which course is a module read in, when the URL does not say?
 *
 * A guess, and unavoidably so: a module can be in several courses and a bare
 * `/module/<id>` carries no way to tell which. First course whose own list
 * contains it, then the module's `track`, then nothing.
 *
 * It lives here rather than inline in ModuleLayout so the legacy redirect and
 * the layout cannot drift into two different guesses — and so it can be tested
 * without rendering anything.
 */
export function courseForModule(moduleId, courses = [], catalogue = []) {
  const list = courses || []
  const inList = list.find((c) => isModuleInCourse(c, moduleId, catalogue))
  if (inList) return inList
  const mod = (catalogue || []).find((m) => String(m.id) === String(moduleId))
  return list.find((c) => String(c.id) === String(mod?.track)) || null
}

/** Is this module in this course? */
export function isModuleInCourse(course, moduleId, catalogue = []) {
  return courseModuleIds(course, catalogue).includes(String(moduleId))
}

/**
 * Modules that could be added to this course: everything not already in it.
 *
 * Nothing is blocked any more, and that is a direct consequence of membership
 * moving onto the course. It used to be that filing a BUILT-IN module into
 * another course meant publishing a Firestore override under its id, which
 * permanently converted a repo-defined module into a data-defined one — a real
 * decision, not something to do from a dropdown, so built-ins were listed
 * disabled. Adding one now appends its id to the course's own list and writes
 * NOTHING to the module. The objection went away with the schema that caused
 * it.
 */
export function addableModules(course, catalogue = []) {
  const already = new Set(courseModuleIds(course, catalogue))
  return (catalogue || []).filter((m) => !already.has(String(m.id)))
}

/**
 * The published modules of a course, in course order.
 *
 * The course-aware replacement for `publishedModules(courseId)`, which resolves
 * membership by filtering the hardcoded registry on `m.track === courseId` and
 * therefore cannot see a course list at all.
 */
export function publishedInCourse(course, catalogue = []) {
  return courseRoster(course, catalogue).filter(isPublished)
}

/** The modules of a course that count towards a completion percentage. */
export function requiredInCourse(course, catalogue = []) {
  return courseRoster(course, catalogue).filter(isRequired)
}

/** True when a module is visible to trainees. */
export function isPublished(mod) {
  if (!mod) return false
  return mod.isCustom ? mod.status === 'published' : mod.published !== false
}

/** True when a module counts towards a completion percentage. */
export function isRequired(mod) {
  return isPublished(mod) && !mod.optional
}

/**
 * Built-ins and custom modules as one list.
 *
 * Every entry carries `isBuiltIn`, `hasOverride`, `isCustom` and a `lessons`
 * array — empty for the code-authored modules, whose sections are markup rather
 * than data and so cannot be listed anywhere.
 */
export function mergeCatalogue(builtIns = [], customs = []) {
  const remaining = new Map((customs || []).map((m) => [String(m.id), m]))
  const merged = []

  for (const base of builtIns || []) {
    const id = String(base.id)
    const override = remaining.get(id)
    merged.push({
      ...base,
      ...(override || {}),
      id,
      isBuiltIn: true,
      hasOverride: !!override,
      isCustom: false,
    })
    remaining.delete(id)
  }

  for (const [, custom] of remaining) {
    merged.push({
      ...custom,
      id: String(custom.id),
      isBuiltIn: false,
      hasOverride: false,
      isCustom: true,
    })
  }

  return merged.map((mod) => ({
    ...mod,
    lessons: Array.isArray(mod.pages) ? mod.pages : [],
  }))
}

/** Every module filed under a course, in catalogue order. */
export function modulesInCourse(catalogue = [], course) {
  // Accepts a course OR a bare id. Given the course it honours its ordered
  // list, which is the only way a module in two courses shows up in both.
  const c = typeof course === 'string' || typeof course === 'number' ? { id: String(course) } : course
  return courseRoster(c, catalogue)
}

/**
 * Courses referenced by a module that the registry has never heard of.
 *
 * `getCourse()` fabricates a course from an unrecognised id rather than failing,
 * and ConfigContext folds it into `allCourses`, so it surfaces on the Degrees
 * page as a real, assignable option. That is a typo becoming curriculum, and
 * the only way to find the cause is to look for the modules pointing at it.
 */
export function phantomCourses(catalogue = [], knownCourseIds = []) {
  const known = new Set(knownCourseIds)
  const byCourse = new Map()

  for (const mod of catalogue || []) {
    if (!mod.track || known.has(mod.track)) continue
    if (!byCourse.has(mod.track)) byCourse.set(mod.track, [])
    byCourse.get(mod.track).push(mod)
  }

  return [...byCourse.entries()].map(([id, modules]) => ({ id, modules }))
}
